import type { AdminRole } from '@goelectric/shared-types';

/**
 * Contrato de autenticación del backoffice, espejo de
 * `services/backend/src/backoffice-crm/auth/`.
 *
 * - `POST /api/v1/admin/auth/login`      → primer factor (email + password). Nunca emite tokens:
 *                             siempre responde `pendingTotp` (2FA obligatorio en roles
 *                             administrativos) y opcionalmente `requireSetup`.
 * - `POST /api/v1/admin/auth/2fa/verify` → segundo factor; emite `accessToken` + `user` y setea la
 *                             cookie httpOnly `refresh_token`.
 * - `GET  /api/v1/admin/auth/me`         → identidad autenticada (Bearer).
 * - `POST /api/v1/admin/auth/refresh`    → rota el refresh y devuelve un nuevo `accessToken` + `user`.
 * - `POST /api/v1/admin/auth/logout`     → revoca la sesión y limpia la cookie (Bearer).
 * - `POST /api/v1/admin/auth/enroll/setup` / `POST /api/v1/admin/auth/enroll/complete` → primer acceso (2FA).
 */

/** Identidad autenticada (espejo de `AuthUser` del backend). */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  /** Rol de API (RBAC US-2.1); la etiqueta visible la resuelve `ROLE_LABELS` de ui-components. */
  role: AdminRole;
  /** Aliado (tenant) del admin. Ausente = plataforma GO ELECTRIC (ADR 0024). */
  aliadoId?: string;
}

/** Respuesta del primer factor de login. */
export interface LoginResponse {
  pendingTotp: boolean;
  /** `true` si el administrador aún no tiene 2FA configurado. */
  requireSetup?: boolean;
  userId?: string;
}

/** Sesión emitida tras completar el segundo factor o un refresh. */
export interface AuthTokensResponse {
  accessToken: string;
  user: AuthUser;
}

/** Respuesta del setup de enrolamiento (QR de la app autenticador). El `secret` va dentro del `otpauthUrl`. */
export interface EnrollSetupResponse {
  otpauthUrl: string;
}

/** Payload de `POST /api/v1/admin/auth/enroll/complete`. */
export interface EnrollCompleteInput {
  email: string;
  enrollmentToken: string;
  newPassword: string;
  code: string;
}

/** Valida que la respuesta de sesión traiga un access token utilizable. */
export function isAuthTokensResponse(value: unknown): value is AuthTokensResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { accessToken?: unknown; user?: unknown };
  return typeof candidate.accessToken === 'string' && candidate.accessToken.length > 0 && typeof candidate.user === 'object' && candidate.user !== null;
}
